/**
 * Draft-ready latency: the SLA while sending is human-gated (F-06).
 *
 * F-06 ruling: "While sending is human-gated the SLA is draft-ready latency
 * (<2 min to a gate-passed draft). Response latency activates per scenario at
 * graduation." `drafts.ready_at` has carried the timestamp since the schema
 * was written, and until this file only the escalation sweep read it, so the
 * one SLA the ruling says is live today was never measured.
 *
 * Pure: plain records in, counts out. No database, no clock, no wiring. The
 * caller decides which inbound message a draft answers and which stored time
 * stands for its receipt; this function only refuses to turn a record it
 * cannot measure into a fast draft.
 *
 * Every record lands in exactly one bucket, checked in this order:
 *   1. anomalous: a timestamp that is present but unreadable (an invalid
 *      Date). Corrupt data is surfaced wherever it sits, never hidden inside
 *      another bucket.
 *   2. unmeasurable: no linked inbound. There is no start to measure from,
 *      and no future readyAt changes that, so this outranks unresolved.
 *   3. unresolved: no readyAt. The draft is not ready, so it is not fast.
 *      Nor is it counted over the bar, even when its inbound is already
 *      older than the bar: with no clock the function cannot tell a late
 *      draft from one not yet due. So `overBar` is a lower bound on breaches,
 *      and a nonzero `unresolved` means the SLA is not shown met.
 *   4. anomalous: readyAt before the inbound receipt. A negative latency is a
 *      clock or linkage fault, never a draft that beat the bar.
 *   5. measured, and over the bar when latency >= DRAFT_READY_BAR_MS.
 *
 * No rate is computed. Counts cannot be rounded into hiding a breach; a rate
 * is the caller's division, done where its rounding is visible.
 */

/**
 * The F-06 bar: a gate-passed draft in under 2 minutes, in milliseconds.
 *
 * "Under" is read strictly, so a draft ready at exactly 120000 ms is over the
 * bar (FINDINGS TA-234). This is a ruled number (CLAUDE.md section 13, F-06);
 * it is not tunable here.
 */
export const DRAFT_READY_BAR_MS = 120_000;

export interface DraftLatencyRecord {
  readonly draftId: string;
  /**
   * The stored receipt time of the inbound message this draft answers, or
   * null when no inbound is linked. Whether that should be the platform's
   * receipt time or this system's ingest time is open (build plan question
   * 10); the function measures from whatever the caller stored.
   */
  readonly inboundReceivedAt: Date | null;
  /**
   * When a gate-passed draft existed, or null if none has yet.
   *
   * Not a straight copy of `drafts.ready_at`: that column defaults to the
   * insert time and `composeHandler.ts` inserts `blocked` drafts too, so a
   * verifier-rejected draft carries a readyAt it never earned. A caller must
   * pass null for any draft that did not pass the gates, or a blocked draft
   * is counted as fast (FINDINGS TA-234). A blocked draft passed as null then
   * stays unresolved for good, though it can never meet the bar.
   */
  readonly readyAt: Date | null;
}

interface BucketCounts {
  readonly total: number;
  readonly unmeasurable: number;
  readonly unresolved: number;
  readonly anomalous: number;
  /** In input order, so the operator can find each one. */
  readonly anomalousDraftIds: readonly string[];
  /** The bar these counts were held to. */
  readonly barMs: number;
}

/**
 * `no_data`: the input was empty. It carries no count at all, so nothing can
 * render it as "0 over the bar".
 *
 * `none_measured`: there were drafts but not one could be measured. The
 * bucket counts are reported and there is no `measured` or `overBar` field,
 * for the same reason.
 *
 * `measured`: at least one draft was measured. `overBar` counts among
 * `measured` only, so it is a lower bound on breaches, not the breach count:
 * `{measured: 1, overBar: 0, unresolved: 40}` is not a met SLA. A caller that
 * wires this must either count, at an as-of time it chooses, each unresolved
 * draft whose inbound is already older than the bar as a breach, or treat any
 * unresolved draft as the bar not met (FINDINGS TA-234).
 */
export type DraftLatencyReport =
  | { readonly kind: "no_data" }
  | ({ readonly kind: "none_measured" } & BucketCounts)
  | ({ readonly kind: "measured"; readonly measured: number; readonly overBar: number } & BucketCounts);

const unreadable = (d: Date | null): boolean => d !== null && Number.isNaN(d.getTime());

export function draftReadyLatency(records: readonly DraftLatencyRecord[]): DraftLatencyReport {
  if (records.length === 0) return { kind: "no_data" };

  let measured = 0;
  let overBar = 0;
  let unmeasurable = 0;
  let unresolved = 0;
  const anomalousDraftIds: string[] = [];

  for (const r of records) {
    if (unreadable(r.inboundReceivedAt) || unreadable(r.readyAt)) {
      anomalousDraftIds.push(r.draftId);
      continue;
    }
    if (r.inboundReceivedAt === null) {
      unmeasurable += 1;
      continue;
    }
    if (r.readyAt === null) {
      unresolved += 1;
      continue;
    }
    const latencyMs = r.readyAt.getTime() - r.inboundReceivedAt.getTime();
    if (latencyMs < 0) {
      anomalousDraftIds.push(r.draftId);
      continue;
    }
    measured += 1;
    if (latencyMs >= DRAFT_READY_BAR_MS) overBar += 1;
  }

  const buckets: BucketCounts = {
    total: records.length,
    unmeasurable,
    unresolved,
    anomalous: anomalousDraftIds.length,
    anomalousDraftIds,
    barMs: DRAFT_READY_BAR_MS,
  };
  if (measured === 0) return { kind: "none_measured", ...buckets };
  return { kind: "measured", measured, overBar, ...buckets };
}
